<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <div
      class="form-section group"
      :data-entity-type="title.trim()">
      <span
        v-if="icon != null"
        class="icon"
        ><component :is="icon"
      /></span>
      {{ title }}
      <slot name="pane-header-right" />
      <div
        v-if="canBeRemoved || canBeClear || canBeToggled || $slots['actions']"
        class="actions">
        <slot name="actions" />
        <button
          v-if="canBeRemoved || canBeClear"
          :disabled="isReadOnly"
          class="btn btn-danger btn-sm"
          @click="$emit('cleared')">
          <template v-if="canBeRemoved">
            <icon-delete />
            {{ capitalize($t('actions.remove')) }}
          </template>
          <template v-else>
            {{ capitalize($t('actions.clear')) }}
          </template>
        </button>
        <component
          :is="!!featureKey && !!entity ? FeatureToggleButton : ToggleButton"
          v-if="canBeToggled"
          v-tooltip="toggleToolTipMessage"
          class="form-section-toggle"
          :feature-key="featureKey"
          :entity="entity"
          :disabled="isToggleDisabled"
          :value="isToggledInternal"
          :sync="true"
          :color="colors.success"
          :labels="{ checked: capitalize($t('states.on')), unchecked: capitalize($t('states.off')) }"
          @change="isToggledInternal = !isToggledInternal" />
      </div>
    </div>
    <div
      v-if="$slots.default && ((canBeToggled && isToggledInternal) || !canBeToggled)"
      class="form-section-content"
      :class="{ 'can-be-toggled': canBeToggled, toggled: isToggledInternal }">
      <slot />
      <div
        v-if="showAdvancedSettingsSlot"
        class="advanced-form-fields-container"
        data-entity-type="advanced-settings">
        <div
          class="advanced-form-fields"
          :class="{ 'scale-vertical-no-delay': showAdvancedFieldsInternal }">
          <form-separator>
            {{ capitalize($t('names.advancedSetting', { n: 2 })) }}
          </form-separator>
          <slot name="advanced-form-fields" />
        </div>
        <button
          class="offset-sm-3 toggle-advanced-form-fields-btn"
          :class="{ expanded: showAdvancedFieldsInternal }"
          @click="showAdvancedFieldsInternal = !showAdvancedFieldsInternal">
          <icon-enlarge />
          <span>
            {{ $t('names.advancedSetting', { n: 2 }) }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
  import { capitalize } from 'vue';

  import colors from '@/Colors';
  import FeatureToggleButton from '@/common/components/controls/FeatureToggleButton.vue';
  import FormSeparator from '@/common/components/controls/FormSeparator';
  import ToggleButton from '@/common/components/controls/ToggleButton';
  import { IconDelete, IconEnlarge } from '@/common/icons/monochrome';

  export default {
    components: {
      FormSeparator,
      IconDelete,
      IconEnlarge,
      ToggleButton,
    },
    props: {
      icon: {
        type: Object,
        default: null,
      },
      title: {
        type: String,
        default: null,
      },
      showAdvancedFields: {
        type: Boolean,
        default: false,
      },
      canBeRemoved: {
        type: Boolean,
        default: false,
      },
      canBeClear: {
        type: Boolean,
        default: false,
      },
      canBeToggled: {
        type: Boolean,
        default: false,
      },
      isReadOnly: {
        type: Boolean,
        default: false,
      },
      showAdvancedSettings: {
        type: Boolean,
        default: true,
      },
      isToggleDisabled: {
        type: Boolean,
        default: false,
      },
      isToggled: {
        type: Boolean,
        default: false,
      },
      featureKey: {
        type: String,
        default: null,
      },
      toggleToolTipMessage: {
        type: String,
        default: null,
      },
      entity: {
        type: Object,
        default: null,
      },
    },
    emits: ['cleared', 'toggled'],
    data() {
      return {
        colors,
        isToggledInternal: false,
        showAdvancedFieldsInternal: false,
      };
    },
    computed: {
      ToggleButton() {
        return ToggleButton;
      },
      FeatureToggleButton() {
        return FeatureToggleButton;
      },
      hasAdvancedFormFieldsSlot() {
        return !!this.$slots['advanced-form-fields'];
      },
      showAdvancedSettingsSlot() {
        if (this.showAdvancedSettings) {
          return this.hasAdvancedFormFieldsSlot;
        }
        return false;
      },
    },
    watch: {
      isToggled(val) {
        this.isToggledInternal = val;
      },
      isToggledInternal() {
        this.$emit('toggled', this.isToggledInternal);
      },
    },
    mounted() {
      this.showAdvancedFieldsInternal = this.showAdvancedFields;
      this.isToggledInternal = this.isToggled;
    },
    beforeCreate() {
      //
      // Assign the same validator as the parent if one exists.
      //
      if (this.$parent != null && this.$parent.$validator != null) {
        this.$validator = this.$parent.$validator;
      }
    },
    methods: {
      capitalize,
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  #app .main-content .form-horizontal {
    .form-section-content {
      &.can-be-toggled {
        padding: 0;

        &.toggled {
          padding: 1.6rem;
        }
      }
    }
  }

  .advanced-form-fields-container {
    display: flex;
    flex-direction: column;
    /*align-items: center;*/

    .toggle-advanced-form-fields-btn {
      // border: dotted 1px var(--secondaryColor);
      color: var(--secondaryColor);
      font-weight: var(--buttonFontWeight);

      border: none;
      transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
      cursor: pointer;
      display: flex;
      flex-direction: row;
      align-items: center;
      background: transparent;
      outline: none;
      font-size: 0.7em;
      text-transform: uppercase;
      border-radius: 1.9rem;
      padding: 0.4rem 0.8rem 0.4rem 0.5rem;
      /*box-shadow: 0 0.1rem 0.3rem rgba(0, 0, 0, 0.15);*/
      margin-top: 1.2rem;
      margin-right: auto;

      &:hover {
        transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);

        background: var(--secondaryColor);
        color: var(--secondaryColorReverse);

        svg {
          stroke: var(--secondaryColorReverse);
          fill: var(--secondaryColorReverse);
        }
      }

      svg {
        transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
        height: 1.8rem;
        width: 1.8rem;

        stroke: var(--secondaryColor);
        fill: var(--secondaryColor);
      }

      span {
        margin-left: 0.6rem;
      }

      &.expanded {
        svg {
          transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
          transform: rotateX(180deg);
        }
      }
    }

    .advanced-form-fields {
      display: block;
      width: 100%;
      /*transform: scaleY(0);*/
      height: 0;
      overflow: hidden;

      &.scale-vertical-no-delay {
        height: auto;
        overflow: initial;
      }
    }
  }
</style>
